import { Container } from 'react-dom/client'

let id = 0
const hash = () => `tw_${(id++).toString(36)}`

export function createFrame({ src }: { src: string }) {
  const frame = document.createElement('iframe')

  frame.dataset.cy = 'frame_tw_js'

  frame.src = src
  frame.id = hash()
  frame.setAttribute(
    'sandbox',
    'allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox'
  )
  frame.setAttribute('allow', 'clipboard-read; clipboard-write')

  frame.style.position = 'fixed'
  frame.style.top = '0'
  frame.style.bottom = '0'
  frame.style.left = '0'
  frame.style.right = '0'
  frame.style.zIndex = '999999999'
  frame.style.border = '0'
  frame.style.padding = '0'
  frame.style.margin = '0'
  frame.style.display = 'revert'
  frame.width = '100%'
  frame.height = '100%'

  frame.title = 'Truework Direct'

  return frame
}

export function mountFrame(frame: HTMLIFrameElement) {
  return new Promise((resolve, reject) => {
    frame.onload = resolve
    frame.onerror = reject
    appendContainer(frame)
  })
}

export function removeFrame(frame: HTMLIFrameElement) {
  document.body.removeChild(frame)
}

export function hideFrame(frame: HTMLIFrameElement) {
  frame.style.display = 'none'
}

// Wait for document.body to become available, create a div, append it to the body, and return the promise.
export async function appendContainer(container: Container = document.createElement('div')): Promise<Container> {
  // When the script is run from <head>, document.body is not available yet.
  if (document.body) {
    document.body.appendChild(container)
    return container
  }
  // When any change is made to document.documentElement, we want to trigger a basic callback that checks whether body
  // exists yet. If body exists, we want to add our container **one time**. Exactly-once callback execution can be
  // hard in JavaScript, so we use a list with an atomic .pop() operation to prevent two events from running at the
  // same time and somehow adding two containers to the body.
  return new Promise((resolve) => {
    // We have a list of callbacks with only one item. This means that when we .pop(), we'll get a callback and the list
    // will become empty, which prevents the critical code from running twice.
    const callbacks = [
      () => {
        observer.disconnect()
        document.body.appendChild(container)
        resolve(container)
      },
    ]

    function checker() {
      // Each time the document node (or its children) change, we run this function. In practice, the first change is
      // *probably* document.body being added, but there's no guarantee. If there's no body element, return early.
      if (!document.body) {
        return
      }

      // At this point we know that there's a body element. Unfortunately it's also possible that body was added and
      // then quickly changed, so it's possible that two event handlers are calling `callbacks.pop()` at the same time.
      const callback = callbacks.pop()
      // Only one will win the race and get a callback to run. The other will just return without doing anything else.
      if (callback) {
        callback()
      }
    }

    const observer = new MutationObserver(checker)

    // Hook up the observer to document.documentElement, which is the root of the tree. This *should* be <html>, but [as
    // usual on the web] there's no strict guarantee that this will be the case. Paranoia rules everything around me.
    observer.observe(document.documentElement, { childList: true })

    // Absolutely top-tier paranoia: it's possible that the body was added to the document while we were setting up our
    // observer above, so now that we've set everything up, we want to run the checker once. We expect that this will
    // do nothing, as the body won't exist, but it's entirely possible that the body *will* exist and that we'll want to
    // pop the callback to prevent double-appending the container. Concurrency is fun.
    checker()
  })
}
